import * as React from 'react';
import { useIntl } from 'react-intl';
import { useRecoilValue } from 'recoil';
import { IRide, IRidePosition, TRideActiveLayer } from '../types';
import {
  createAlarmPositionMarkerTooltips,
  createCombinedAlarmIconMarkers,
  IRideMapAlarmPositionMarkerTooltips,
  IRideMapCombinedAlarmMarkers,
  updateAlarmIconMarkersWithNewPosition,
} from '../RidePlayerAlarmMarkerService';
import { IS_ALARM_LAYER } from '../constants';
import { activeRideNumberAtom, selectedRidesAtom } from '../RidePlayerState';
import { hideAllTooltips } from '../RidePlayerMarkerService';

interface IUseAlarmMarker {
  alarmMarkers: IRideMapCombinedAlarmMarkers;
  alarmPositionMarkerTooltips: IRideMapAlarmPositionMarkerTooltips;
  showAlarms: boolean;
  setShowAlarms: React.Dispatch<React.SetStateAction<boolean>>;
  showAllAlarmIconMarkers: () => void;
  handleToggleShowAlarms: (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => void;
  setAlarmMarkersOpacity: (rideNumber: number, opacity: number) => void;
  setAlarmMarkerOpacity: (
    rideNumber: number,
    position: google.maps.LatLng,
    opacity: number
  ) => void;
  setAllAlarmIconMarkersOpacity: (opacity: number, exceptRideNumber?: number) => void;
  toggleAllAlarmPositionMarkerTooltips(toggleValue: boolean): void;
  hideAllAlarmIconMarkers: () => void;
  removeAllAlarmMarkers: () => void;
}

/**
 * useAlarmMarker custom hook
 * @param {google.maps.Map} map
 * @param {IRide[]} rides
 * @param {number} zoomLevel
 * @returns {IUseAlarmMarker}
 */
const useAlarmMarker = (
  map: google.maps.Map,
  rides: IRide[],
  activeLayer: TRideActiveLayer,
  zoomLevel: number,
  actualRidePosition?: IRidePosition | null
): IUseAlarmMarker => {
  const intl = useIntl();
  const [showAlarms, setShowAlarms] = React.useState<boolean>(true);
  const alarmMarkers = React.useRef<IRideMapCombinedAlarmMarkers>({ 1: {} });
  const alarmPositionMarkerTooltips = React.useRef<IRideMapAlarmPositionMarkerTooltips>({
    1: [],
  });
  const selectedRides = useRecoilValue<number[]>(selectedRidesAtom);
  const activeRide = useRecoilValue<number>(activeRideNumberAtom);

  /**
   * Show all alarm icon markers
   * @returns void
   */
  const showAllAlarmIconMarkers = (): void => {
    Object.keys(alarmMarkers.current).forEach((rideNumber: string) => {
      Object.keys(alarmMarkers.current[Number(rideNumber)]).forEach((positionIndex: string) => {
        const markerData = alarmMarkers.current[Number(rideNumber)][Number(positionIndex)];
        if (
          markerData &&
          !markerData.marker.getMap() &&
          selectedRides.includes(Number(rideNumber))
        ) {
          markerData.marker.setMap(map);
        }
      });
    });
  };

  /**
   * Hide all alarm icon markers
   * @returns void
   */
  const hideAllAlarmIconMarkers = (): void => {
    Object.keys(alarmMarkers.current).forEach((rideNumber: string) => {
      Object.keys(alarmMarkers.current[Number(rideNumber)]).forEach((positionIndex: string) => {
        const markerData = alarmMarkers.current[Number(rideNumber)][Number(positionIndex)];
        if (markerData && markerData.marker.getMap()) {
          markerData.marker.setMap(null);
        }
      });
    });
  };

  /**
   * Show/hide all alarm position marker tooltips
   * @param {boolean} toggleValue
   * @returns void
   */
  const toggleAllAlarmPositionMarkerTooltips = (toggleValue: boolean): void => {
    Object.keys(alarmPositionMarkerTooltips.current).forEach((rideNumber: string) => {
      alarmPositionMarkerTooltips.current[Number(rideNumber)].forEach(
        (tooltip: google.maps.OverlayView | null) => {
          if (tooltip && toggleValue && !tooltip.getMap()) {
            tooltip.setMap(map);
          }
          if (tooltip && !toggleValue && tooltip.getMap()) {
            tooltip.setMap(null);
          }
        }
      );
    });
  };

  /**
   * Toggle alarm tooltip
   * @param {number} rideNumber
   * @param {number} positionIndex
   * @returns {void}
   */
  const handleToggleAlarmPositionTooltip = (rideNumber: number, positionIndex: number): void => {
    const alarmPositionTooltip = alarmPositionMarkerTooltips.current[rideNumber][positionIndex];
    hideAllTooltips(alarmPositionMarkerTooltips, positionIndex);
    if (alarmPositionTooltip) {
      alarmPositionTooltip.setMap(alarmPositionTooltip.getMap() ? null : map);
    }
  };

  /**
   * Toggle show alarms
   * @param {React.MouseEvent<HTMLAnchorElement, MouseEvent>} event
   * @returns {void}
   */
  const handleToggleShowAlarms = (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>): void => {
    event.preventDefault();
    setShowAlarms(!showAlarms);
  };

  /**
   * Set all alarm markers opacity for specified ride
   * @param {number} rideNumber
   * @param {number} opacity
   * @returns {void}
   */
  const setAlarmMarkersOpacity = (rideNumber: number, opacity: number): void => {
    Object.keys(alarmMarkers.current[Number(rideNumber)]).forEach((positionIndex: string) => {
      const markerData = alarmMarkers.current[Number(rideNumber)][Number(positionIndex)];
      if (markerData) {
        markerData.marker.setOpacity(opacity);
      }
    });
  };

  /**
   * Set alarm marker opacity by position and ride
   * @param {number} rideNumber
   * @param {google.maps.LatLng} position
   * @param {number} opacity
   * @returns {void}
   */
  const setAlarmMarkerOpacity = (
    rideNumber: number,
    position: google.maps.LatLng,
    opacity: number
  ): void => {
    Object.keys(alarmMarkers.current[Number(rideNumber)]).forEach((positionIndex: string) => {
      const markerData = alarmMarkers.current[Number(rideNumber)][Number(positionIndex)];
      if (markerData && markerData.marker) {
        const pos = markerData.marker.getPosition();
        if (pos && pos.toString() === position.toString()) {
          markerData.marker.setOpacity(opacity);
        }
      }
    });
  };

  /**
   * Set all alarm icon markers opacity for all rides
   * @param {number} opacity
   * @param {number} exceptRideNumber
   * @returns {void}
   */
  const setAllAlarmIconMarkersOpacity = (opacity: number, exceptRideNumber?: number): void => {
    if (alarmMarkers.current) {
      Object.keys(alarmMarkers.current).forEach((rideNumber: string) => {
        if (exceptRideNumber && exceptRideNumber === Number(rideNumber)) {
          return;
        }
        Object.keys(alarmMarkers.current[rideNumber]).forEach((positionIndex: string) => {
          const marker = alarmMarkers.current[rideNumber][positionIndex].marker;
          marker.setOpacity(opacity);
        });
      });
    }
  };

  const removeAllAlarmMarkers = () => {
    hideAllAlarmIconMarkers();
    alarmMarkers.current = {};
  };

  // Handle show/hide alarm markers by selected rides
  React.useEffect(() => {
    Object.keys(alarmMarkers.current).forEach((rideNumber: string) => {
      Object.keys(alarmMarkers.current[Number(rideNumber)]).forEach((positionIndex: string) => {
        const marker = alarmMarkers.current[Number(rideNumber)][positionIndex].marker;
        if (!selectedRides.includes(Number(rideNumber)) && marker.getMap()) {
          marker.setMap(null);
        }
        if (
          selectedRides.includes(Number(rideNumber)) &&
          !marker.getMap() &&
          activeLayer === IS_ALARM_LAYER
        ) {
          marker.setMap(map);
        }
      });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeLayer, map, selectedRides]);

  React.useEffect(() => {
    alarmMarkers.current = createCombinedAlarmIconMarkers(
      activeLayer === IS_ALARM_LAYER ? map : undefined,
      rides,
      handleToggleAlarmPositionTooltip,
      activeRide
    );
    alarmPositionMarkerTooltips.current = createAlarmPositionMarkerTooltips(rides, intl);

    // Unset alarm markers and tooltips from map
    return () => {
      hideAllAlarmIconMarkers();
      alarmMarkers.current = { 1: [] };
      toggleAllAlarmPositionMarkerTooltips(false);
      alarmPositionMarkerTooltips.current = { 1: [] };
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // If actual position is present, update alarm marker and tooltip
  React.useEffect(() => {
    if (actualRidePosition && activeRide) {
      const positionIndex = rides[activeRide - 1].positions.length - 1;
      const ride = rides[activeRide - 1];
      updateAlarmIconMarkersWithNewPosition(
        activeLayer === IS_ALARM_LAYER ? map : undefined,
        alarmMarkers.current,
        ride,
        activeRide,
        actualRidePosition,
        positionIndex,
        handleToggleAlarmPositionTooltip
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [actualRidePosition, activeRide, map]);

  const data: IUseAlarmMarker = {
    alarmMarkers: alarmMarkers.current,
    alarmPositionMarkerTooltips: alarmPositionMarkerTooltips.current,
    showAlarms,
    setShowAlarms,
    showAllAlarmIconMarkers,
    handleToggleShowAlarms,
    setAlarmMarkersOpacity,
    setAlarmMarkerOpacity,
    toggleAllAlarmPositionMarkerTooltips,
    hideAllAlarmIconMarkers,
    setAllAlarmIconMarkersOpacity,
    removeAllAlarmMarkers,
  };

  return data;
};

export default useAlarmMarker;
